"use client";

/**
 * AnchorScan — the Anchor cluster-scan animation from
 * `docs/Fable UiUx review/wtm-components-telemetry-galton-anchor.html`
 * (Component 03).
 *
 * A field of ticker nodes, a weekly sweep line passing over it, and the
 * discovered names condensing into a few named, scored themes. The visual
 * says "discovery": Compass doesn't just monitor your watchlist — it hunts
 * for what should be on it.
 *
 * REGULATORY LINE (do not cross): the output is THEMES with evidence,
 * never stock buy/sell recommendations. The default content is clearly
 * ILLUSTRATIVE (example theme names, visible "Illustrative scan" caption).
 *
 * TODO(page-wiring / later batch): pass real, sanitized Anchor scan output
 * via the `themes` prop (last week's genuine theme names + scores, scrubbed
 * of any advisory language) and set `illustrative={false}` — the caption
 * switches to the `caption` prop text. Until real sanitized data exists,
 * keep the defaults: a fabricated "live" scan would burn more trust than
 * the component earns.
 *
 * Architecture: pure stage functions over `useStageDriver` —
 *   Stage 0 SWEEP      the scan line crosses the field; passed nodes resolve
 *   Stage 1 CONDENSE   selected nodes glide to their theme centroids
 *   Stage 2 REVEAL     edges + ticker labels, then theme names/scores stagger in
 *   Stage 3 HOLD       the finished scan rests before the loop restarts
 * Reduced motion pins to the final clustered, labeled state.
 */

import { useMemo } from "react";
import { useStageDriver } from "@/lib/motion";
import { cn } from "@/lib/utils/cn";
import {
  MONO,
  EASE,
  LINE,
  easeOutCubic,
  lerp,
  stageLocalProgress,
} from "./shared";

export interface AnchorScanTheme {
  /** Theme name, e.g. "AI MEMORY CYCLE". Keep it a theme — never advice. */
  name: string;
  /** Theme score 0-10 (Anchor's Monitor List scoring scale, /10). */
  score: number;
  /** Member tickers shown inside the cluster (max 4 rendered). */
  tickers: string[];
}

export interface AnchorScanProps {
  /**
   * Up to 3 themes. Defaults to ILLUSTRATIVE examples — see the TODO in
   * the file header before wiring real data.
   */
  themes?: AnchorScanTheme[];
  /**
   * True (default) renders the "ILLUSTRATIVE SCAN" caption. Only set false
   * when `themes` carries real, sanitized Anchor output.
   */
  illustrative?: boolean;
  /** Caption text when `illustrative` is false (e.g. "From last week's scan"). */
  caption?: string;
  /** Total loop length in ms (default 12000). */
  cycleMs?: number;
  className?: string;
}

/** Clearly-illustrative defaults (mirror the approved prototype copy). */
const ILLUSTRATIVE_THEMES: AnchorScanTheme[] = [
  { name: "AI MEMORY CYCLE", score: 8.2, tickers: ["MU", "WDC", "STX", "AVGO"] },
  { name: "GLP-1 SUPPLY CHAIN", score: 7.6, tickers: ["LLY", "NVO", "WST", "TMO"] },
  { name: "GRID CAPEX", score: 7.1, tickers: ["ETN", "PWR", "HUBB", "VRT"] },
];

const W = 460;
const H = 280;
const CENTROIDS: Array<[number, number]> = [
  [110, 95],
  [338, 90],
  [230, 190],
];
const DEFAULT_DURATIONS = [4000, 1200, 2200, 4600];
const DEFAULT_TOTAL = DEFAULT_DURATIONS.reduce((a, b) => a + b, 0);

interface ScanNode {
  x: number;
  y: number;
  sel: boolean;
  cluster?: number;
  ticker?: string;
  tx?: number;
  ty?: number;
}

/** Deterministic scatter (seeded LCG) — identical on server + client. */
function buildField(themes: AnchorScanTheme[]): ScanNode[] {
  let seed = 7;
  const rnd = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  const nodes: ScanNode[] = [];
  for (let i = 0; i < 42; i++) {
    nodes.push({ x: 20 + rnd() * (W - 40), y: 25 + rnd() * (H - 55), sel: false });
  }
  themes.forEach((theme, ci) => {
    const c = CENTROIDS[ci];
    theme.tickers.slice(0, 4).forEach((tk, k) => {
      nodes.push({
        x: 20 + rnd() * (W - 40),
        y: 25 + rnd() * (H - 55),
        sel: true,
        cluster: ci,
        ticker: tk,
        tx: c[0] + 22 * Math.cos((k / 4) * 2 * Math.PI + 0.6),
        ty: c[1] + 22 * Math.sin((k / 4) * 2 * Math.PI + 0.6),
      });
    });
  });
  return nodes;
}

export function AnchorScan({
  themes = ILLUSTRATIVE_THEMES,
  illustrative = true,
  caption,
  cycleMs,
  className,
}: AnchorScanProps) {
  const clamped = useMemo(() => themes.slice(0, CENTROIDS.length), [themes]);
  const nodes = useMemo(() => buildField(clamped), [clamped]);

  const durations = useMemo(() => {
    const scale = cycleMs && cycleMs > 0 ? cycleMs / DEFAULT_TOTAL : 1;
    return DEFAULT_DURATIONS.map((d) => d * scale);
  }, [cycleMs]);

  const driver = useStageDriver<HTMLDivElement>({ durations, reactStateFps: 30 });
  const { frame, reducedMotion } = driver;
  const sp = (i: number) => stageLocalProgress(frame, i);

  const sweepX = W * sp(0);
  const sweeping = !reducedMotion && frame.stage === 0;
  const condenseE = easeOutCubic(sp(1));
  const detailOn = sp(2) > 0.04;
  // Theme labels stagger in across stage 2.
  const themeShown = (i: number) => sp(2) >= 0.12 + i * 0.24;
  const surfaced = clamped.reduce((acc, _, i) => acc + (themeShown(i) ? 1 : 0), 0);

  const status =
    frame.stage === 0 && !reducedMotion
      ? "● SCANNING"
      : frame.stage === 1
        ? "● CLUSTERING"
        : `● ${surfaced} THEME${surfaced === 1 ? "" : "S"} SURFACED`;

  const captionText = illustrative
    ? "ILLUSTRATIVE SCAN: example themes, not recommendations"
    : caption ?? "FROM A RECENT ANCHOR SCAN: themes with evidence, not recommendations";

  return (
    <div
      ref={driver.ref}
      className={cn(
        "overflow-hidden rounded-[14px] border bg-[#0A101F] shadow-[0_24px_60px_-24px_rgba(0,0,0,.55)]",
        className,
      )}
      style={{ borderColor: LINE }}
      aria-label="Animated illustration of Anchor's weekly discovery scan condensing tickers into scored themes"
    >
      {/* Header */}
      <div
        className="flex flex-wrap items-center gap-3 border-b px-5 py-3.5 font-tabular text-[0.66rem] tracking-[0.1em] text-[#64748B]"
        style={{ fontFamily: MONO, borderColor: LINE }}
      >
        <span className="rounded-full border border-gold/50 px-[11px] py-[3px] text-gold">
          ANCHOR
        </span>
        <span>WEEKLY DISCOVERY SCAN</span>
        <span className="ml-auto text-cgreen">{status}</span>
      </div>

      {/* Field */}
      <svg viewBox={`0 0 ${W} ${H}`} aria-hidden className="block w-full">
        {/* nodes */}
        {nodes.map((n, i) => {
          const scanned = !sweeping || n.x <= sweepX;
          const cx = n.sel ? lerp(n.x, n.tx!, condenseE) : n.x;
          const cy = n.sel ? lerp(n.y, n.ty!, condenseE) : n.y;
          return (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={n.sel ? 3 : 2}
              fill={
                scanned
                  ? n.sel
                    ? "#F59E0B"
                    : "rgba(148,163,184,.12)"
                  : "rgba(148,163,184,.3)"
              }
              style={{ transition: `fill 400ms ${EASE}` }}
            />
          );
        })}
        {/* centroid→member edges + ticker labels */}
        {nodes.map((n, i) =>
          n.sel ? (
            <g key={`d${i}`} style={{ opacity: detailOn ? 1 : 0, transition: `opacity 600ms ${EASE}` }}>
              <line
                x1={CENTROIDS[n.cluster!][0]}
                y1={CENTROIDS[n.cluster!][1]}
                x2={n.tx}
                y2={n.ty}
                stroke="rgba(245,158,11,.25)"
                strokeWidth={0.8}
              />
              <text
                x={n.tx! + 7}
                y={n.ty! + 3}
                style={{ fontFamily: MONO, fontSize: 7.5, fill: "#94A3B8" }}
              >
                {n.ticker}
              </text>
            </g>
          ) : null,
        )}
        {/* theme labels */}
        {clamped.map((t, i) => {
          const shown = themeShown(i);
          const [cx, cy] = CENTROIDS[i];
          return (
            <g key={t.name} style={{ opacity: shown ? 1 : 0, transition: `opacity 500ms ${EASE}` }}>
              <text
                x={cx}
                y={cy - 43}
                textAnchor="middle"
                style={{ fontFamily: MONO, fontSize: 10, fill: "#F1F5F9", letterSpacing: "1px" }}
              >
                {t.name}
              </text>
              <text
                x={cx}
                y={cy - 31}
                textAnchor="middle"
                className="font-tabular"
                style={{ fontFamily: MONO, fontSize: 9, fill: "#F59E0B", letterSpacing: ".5px" }}
              >
                THEME SCORE {t.score.toFixed(1)} · {Math.min(t.tickers.length, 4)} NAMES
              </text>
            </g>
          );
        })}
        {/* sweep line */}
        <line
          x1={sweepX}
          y1={10}
          x2={sweepX}
          y2={H - 10}
          stroke="rgba(59,130,246,.12)"
          strokeWidth={26}
          opacity={sweeping ? 1 : 0}
        />
        <line
          x1={sweepX}
          y1={10}
          x2={sweepX}
          y2={H - 10}
          stroke="rgba(59,130,246,.7)"
          strokeWidth={1.2}
          opacity={sweeping ? 1 : 0}
        />
      </svg>

      {/* Footer */}
      <div
        className="flex flex-wrap gap-x-[18px] gap-y-1 border-t px-5 py-2.5 font-tabular text-[0.62rem] tracking-[0.08em] text-[#64748B]"
        style={{ fontFamily: MONO, borderColor: LINE }}
      >
        <span>
          SIGNALS · <b className="font-medium text-[#94A3B8]">NEWS VELOCITY · FILINGS · FLOWS · MACRO</b>
        </span>
        <span>
          SURFACED · <b className="font-medium text-[#94A3B8]">{surfaced} THEMES</b>
        </span>
        <span>
          CADENCE · <b className="font-medium text-[#94A3B8]">WEEKLY</b>
        </span>
        <span className="basis-full text-gold/80 sm:ml-auto sm:basis-auto">{captionText}</span>
      </div>
    </div>
  );
}
